import React from 'react';
import { cn, Tag, Typography } from '@kiiwi/ui';

import { CardHcdAvailabilityOutputDTO } from '@maiia/model/generated/model/api-patient/api-patient';
import { round } from 'lodash';
import FontAwesomeIcon from '@/components/atoms/FontawesomeIcon/FontawesomeIcon';
import { useTranslation } from '@/src/i18n';
import useResponsive from '@/src/hooks/useResponsive';
import ProCardAvatar from '@/components/organisms/ProCard/ProCardAvatar';
import ProCardName from '@/components/organisms/ProCard/ProCardName';

const SECTORS = {
  SECTOR1: 'convention_sector_1',
  SECTOR1_DP_OPTAM: 'convention_sector_1',
  SECTOR2: 'convention_sector_2',
  SECTOR2_OPTAM: 'convention_sector_2',
  SECTOR2_OPTAM_CO: 'convention_sector_2',
  AFFILIATED: 'convention_sector_affiliated',
};

interface ProCardInfosProps {
  item: CardHcdAvailabilityOutputDTO;
  specialityName?: string;
  goToHcdPage: React.ReactEventHandler;
  onClickInfos?: React.ReactEventHandler;
  onClickName?: React.ReactEventHandler;
  onClickAvatar?: React.ReactEventHandler;
}

const ProCardInfos = ({
  item,
  specialityName,
  goToHcdPage,
  onClickInfos,
  onClickName,
  onClickAvatar,
}: ProCardInfosProps) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const {
    id,
    distance,
    speciality,
    allowNewPatient = true,
    sectors = [],
    specialityCounts = [],
    type,
    address,
  } = item;
  const { number = '', street = '', zipCode = '', city = '' } = address || {};

  const numberAndStreet = `${number} ${street}`.trim() || null;
  const postalCodeAndCity = `${zipCode} ${city}`.trim() || null;
  const sectorTags = sectors.filter(c => c in SECTORS).map(c => SECTORS[c]);
  const specialityCount =
    (specialityName &&
      specialityCounts.find(c => c.speciality === specialityName)?.count) ||
    0;

  const hasSectorTags = sectors.length > 0;
  const hasPatientLimitation = !allowNewPatient;
  const hasSpecialityCounter = type === 'CENTER' && specialityCount > 0;

  const onClickDestopAvatar = (e: React.MouseEvent) => {
    onClickAvatar?.(e);
    goToHcdPage(e);
  };

  return (
    <div
      className="flex w-full flex-1 gap-4 overflow-hidden"
      onClick={onClickInfos}
    >
      <ProCardAvatar
        className={cn(isMobile ? 'hidden' : 'block')}
        item={item}
        onClick={onClickDestopAvatar}
      />
      <div className="flex flex-1 flex-col gap-3 overflow-hidden">
        <div className="flex gap-2 overflow-hidden">
          <ProCardAvatar
            className={cn(isMobile ? 'block' : 'hidden')}
            item={item}
            size="xs"
            onClick={onClickAvatar}
          />
          <div className="flex-1 overflow-hidden">
            <ProCardName item={item} onClick={onClickName} />
            <Typography variant="body-1" className="text-sm sm:text-base">
              {speciality}
            </Typography>
          </div>
        </div>

        <div>
          {(distance || numberAndStreet) &&
            (distance ? (
              <div className="flex flex-row items-center gap-1 text-sm text-neutral-secondary sm:text-base">
                <FontAwesomeIcon
                  name="location-arrow:solid"
                  className="inline h-3 min-h-3"
                />

                <Typography variant="body-1">
                  <span className="font-semibold">
                    {`${round(distance, 1)} km${numberAndStreet ? ' • ' : ''}`}
                  </span>

                  {numberAndStreet}
                </Typography>
              </div>
            ) : (
              <Typography
                variant="body-1"
                className="truncate text-sm text-neutral-secondary sm:text-base"
              >
                {numberAndStreet}
              </Typography>
            ))}

          <Typography
            variant="body-1"
            className="truncate text-sm text-neutral-secondary sm:text-base"
          >
            {postalCodeAndCity}
          </Typography>
        </div>

        {(hasSectorTags || hasPatientLimitation || hasSpecialityCounter) && (
          <div
            className={cn('flex gap-2', isMobile ? 'flex-wrap' : 'flex-col')}
          >
            {sectorTags.map(c => (
              <Tag key={`${id}-sectors-${c}`} color="green" className="h-5">
                {t(c)}
              </Tag>
            ))}

            {hasPatientLimitation && (
              <Tag color="orange" className="h-5">
                {t('pro_card_is_patient_limited')}
              </Tag>
            )}

            {hasSpecialityCounter && (
              <Tag className="h-5">{`${specialityName} (${specialityCount})`}</Tag>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(
  ProCardInfos,
  (oldProps, newProps) => oldProps.item.id === newProps.item.id,
);
